"use client";

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Text from "../../typography";
import ReactHtmlParser from "react-html-parser";
import { Grid, Box } from "@mui/material";
import styled from "@emotion/styled";
import refIds from "../../../util/refIds";
import Cta from "../../ui/Cta";

const CtaItem = styled.div`
  display: inline-block;
  ${({ align }) =>
    align === "left" ? "margin: 10px 10px 10px 0;" : "margin: 10px;"}
`;

const getScripts = (content) => {
  if (typeof content !== "string" || !content) return [];

  const re = /<script.*?src="(.*?)"/gm;
  let match;
  const scripts = [];

  while ((match = re.exec(content))) {
    if (match?.[1] && typeof match[1] === "string") {
      scripts.push(match[1]);
    }
  }

  return scripts;
};

const isMyScriptLoaded = (url) => {
  if (typeof document === "undefined" || typeof url !== "string") return false;

  const scripts = document.getElementsByTagName("script");
  for (let i = 0; i < scripts.length; i += 1) {
    if (scripts[i].src === url) return true;
  }

  return false;
};

const bodyTextDefaultPropCtas = [];
const bodyTextDefaultPropHeaderProps = {};
const bodyTextDefaultPropOnCtaClick = () => {};

const safeString = (value, fallback = "") => {
  return typeof value === "string" ? value : fallback;
};

const safeArray = (value) => {
  return Array.isArray(value) ? value : [];
};

const BodyText = (incomingProps) => {
  const props = {
    ...incomingProps,
    title:
      typeof incomingProps.title === "undefined" ? null : incomingProps.title,
    content:
      typeof incomingProps.content === "undefined"
        ? null
        : incomingProps.content,
    ctas:
      typeof incomingProps.ctas === "undefined"
        ? bodyTextDefaultPropCtas
        : incomingProps.ctas,
    align:
      typeof incomingProps.align === "undefined"
        ? "center"
        : incomingProps.align,
    headerType:
      typeof incomingProps.headerType === "undefined"
        ? "h2"
        : incomingProps.headerType,
    headerProps:
      typeof incomingProps.headerProps === "undefined"
        ? bodyTextDefaultPropHeaderProps
        : incomingProps.headerProps,
    onCtaClick:
      typeof incomingProps.onCtaClick === "undefined"
        ? bodyTextDefaultPropOnCtaClick
        : incomingProps.onCtaClick,
    secondary:
      typeof incomingProps.secondary === "undefined"
        ? false
        : incomingProps.secondary,
  };

  const [refId, setRefId] = useState("");

  const {
    title,
    content,
    ctas,
    align,
    headerType,
    headerProps,
    onCtaClick,
    secondary,
    hTag,
  } = props;

  const safeTitle = safeString(title, "");
  const safeContent = safeString(content, "");
  const safeAlign = typeof align === "string" ? align : "center";
  const safeCtas = safeArray(ctas);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!process.env.DEALER_SITE) return;

    const website = window?.staticly?.dealerData?.website;
    if (typeof website !== "string") return;

    const ref = typeof refIds?.[website] === "string" ? refIds[website] : "";
    setRefId(ref);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const scripts = getScripts(safeContent);

    scripts.forEach((item) => {
      const onPage = isMyScriptLoaded(item);

      if (onPage) {
        window.location.reload();
        return;
      }

      const script = document.createElement("script");
      script.src = item;
      script.id = item;
      script.async = true;
      document.body.appendChild(script);
    });
  }, [safeContent]);

  const parsedContent = useMemo(() => {
    if (!safeContent) return null;
    return ReactHtmlParser(safeContent);
  }, [safeContent]);

  return (
    <Grid
      container
      className="hasPadding"
      spacing={0}
      direction="row"
      wrap="nowrap"
      justifyContent="space-between"
      align={safeAlign}
    >
      <Grid size={12}>
        <Box px={2}>
          {safeTitle && (
            <Text
              variant={headerType}
              element={hTag || headerType}
              size="large"
              colour="charcoal"
              uppercase
              align={safeAlign}
              {...headerProps}
            >
              {safeTitle}
            </Text>
          )}

          {safeContent && (
            <Text component="div" element="div" variant="body1" align={safeAlign}>
              {parsedContent}
            </Text>
          )}

          {safeCtas.map((item, index) => {
            const href =
              typeof item?.href === "string"
                ? item.href.replace("[refId]", refId)
                : "";

            const titleText =
              typeof item?.title === "string" ? item.title : "";

            const target =
              typeof item?.target === "string" ? item.target : undefined;

            const colour =
              typeof item?.colour === "string" ? item.colour : null;

            if (!href || !titleText) return null;

            return (
              <CtaItem
                key={index}
                align={safeAlign}
                onClick={() => onCtaClick(item)}
              >
                <Cta
                  href={href}
                  target={target}
                  inline
                  secondary={index % 2 === 0 ? false : secondary}
                  bg={index % 2 === 0 ? colour : null}
                  color={index % 2 !== 0 ? colour : null}
                  outline={index % 2 !== 0}
                >
                  {titleText}
                </Cta>
              </CtaItem>
            );
          })}
        </Box>
      </Grid>
    </Grid>
  );
};

BodyText.propTypes = {
  title: PropTypes.string,
  content: PropTypes.string,
  ctas: PropTypes.array,
  align: PropTypes.string,
  headerType: PropTypes.string,
  headerProps: PropTypes.object,
  onCtaClick: PropTypes.func,
  secondary: PropTypes.bool,
};

export default BodyText;